import * as React from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers';
import { makeStyles } from '@material-ui/core';
import { Collapse } from 'react-collapse';
import UsersForm from './UsersForm';
import AddContactWizard from './AddContactWizard';
import ContactSearchList from '../organisms/ContactSearchList';
import useTablePage from '../hooks/useTablePage';
import { updateProject } from '../api/projects';
import ModalInfo from '../molecules/ModalInfo';
import RichTextEditorWithLabel from '../molecules/RichTextEditorWithLabel';
import EboxStatus from '../molecules/EboxStatus';
import Table from '../organisms/Table';
import { DEFAULT_DATE_FORMAT, GET_ALL_QUERY_PARAMS } from '../constants';

import {
  InputTextWithLabel,
  InputBaseContainer,
  TextAreaWithLabel,
  InputSelectWithLabel,
  InputCurrencyWithLabel,
  ButtonWithLoader,
  LoaderWithContainer,
  ModalDefaultDraggable,
  InputDatePickerWithLabel,
} from '../molecules';
import SwitchCheckbox from '../modules/clientModule/components/Common/Controls/SwitchCheckbox';
import { getContactTypeList } from '../api/contactType';

import { VocabularyDataType } from '../api/vocabulary';
import { PermitType, getPermitActivitiesInspectionStatus } from '../api/permits';
import { ContactTypesType } from '../api/contactType';
import { UserType, getPermitUsers, getUsersCustom, createUser, getUserByID } from '../api/users';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import { ButtonMain } from '../atoms';
import { getProjectByID } from '../api/projects';
import { USER_TYPES_VALUES, CLIENT_TYPES_VALUES } from '../constants/forms';
import ActionsPanel from '../organisms/ActionsPanel';
import formatDate from '../utils/formatDate';
import tableColumnsFormatter from '../utils/tableColumnsFormatter';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/root';
import { setIsViewContactsModal, setPermitContacts } from '../redux/slices/projectSlice';
import { setPermitUsers } from '../redux/slices/inspectionsSlice';
import { getAccountingInfo } from '../utils';
import PermitFormPermitLink from './PermitFormPermitLink';
import { urlRegex } from '../regex';
import { useState } from 'react';
const useStyles = makeStyles(theme => ({
  button: {
    marginRight: theme.spacing(1),
  },
}));

const labels = {
  projectName: 'Project Name',
  name: 'Permit Name',
  number: 'Permit Number',
  total: 'Contract Total',
  square: 'Square Feet',
  gateCode: 'Gate Code',
  inspections: 'Count of Inspections',
  folio: 'Folio/Property ID',
  storageBox: 'Storage Box Number',
  permitCost: 'Permit Cost',
  jurisdiction: 'Jurisdiction',
  documents: 'Permit Supporting Documents',
  address: 'Address',
  description: 'Description',
  info: 'Permit Info',
  status: 'Permit Status',
  permitContacts: 'Contacts',
  folderName: 'Folder Name',
  serviceType: 'Type of Service',
  manager: 'Project Manager',
  work: 'Work',
  building: 'Building',
  expirationDate: 'Expiration Date',
  permitIssued: 'Permit Issued',
  approvedPlans: 'Approved Plans',
};

const validationSchema = yup.object().shape({
  projectName: yup.string().label(labels.projectName),
  name: yup.string().required().label(labels.name),
  company: yup.object(),
  number: yup.string().label(labels.number),
  total: yup.string().label(labels.total).nullable(),
  permitLink: yup.string().matches(urlRegex, 'Url has a wrong format, need to start with http:// or https://').label('Permit Link'),
  square: yup.number().typeError('This field must include only digits').label(labels.square),
  gateCode: yup.string().label(labels.gateCode),
  folio: yup.string().label(labels.folio),
  inspections: yup
    .number()
    .transform((cv, ov) => {
      return ov === '' ? undefined : cv;
    })
    .typeError('Number Inspections must be a number')
    .nullable()
    .label(labels.inspections),
  // Making storage box number not required field
  storageBox: yup
    .number()
    .typeError('This field must include only digits')
    .nullable()
    .transform((_, val) => (val !== '' && val ? Number(val) : null))
    .label(labels.storageBox),
  permitCost: yup
    .number()
    .typeError('This field must include only digits')
    .nullable()
    .transform((_, val) => (val !== '' && val ? Number(val) : null))
    .label(labels.permitCost),
  jurisdiction: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      }),
    )
    .typeError('Select a Jurisdictions from the list')
    .nullable()
    .label(labels.jurisdiction),
  documents: yup.string().label(labels.documents),
  address: yup.string().required().label(labels.address),
  description: yup
    .string()
    .transform(value => {
      // Strip out empty HTML tags & whitespace
      const cleaned = value?.replace(/<(.|\n)*?>/g, '').trim();
      return cleaned === '' ? '' : value;
    })
    .required('Description is a required field')
    .label(labels.description),

  info: yup.string().label(labels.info),
  status: yup
    .object()
    .shape({ value: yup.string(), label: yup.string() })
    .required()
    .typeError('Select a permit status')
    .label(labels.work),
  permitContacts: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      }),
    )
    .required()
    .typeError('Select a Contact Names from the list')
    .label(labels.permitContacts),
  serviceType: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      }),
    )
    .required()
    .typeError('Select a service type'),
  manager: yup.string().label(labels.manager),
  permitIssued: yup.boolean(),
  work: yup
    .object()
    .shape({ value: yup.string(), label: yup.string() })
    .required()
    .typeError('Select a work from the list')
    .nullable()
    .label(labels.work),
  building: yup
    .object()
    .shape({ value: yup.string(), label: yup.string() })
    .required()
    .typeError('Select a building from the list')
    .label(labels.building),
});

const PermitForm = ({
  initialValues,
  // selectedPermit,
  permitStatus,
  allContacts,
  contactTypes,
  serviceTypes,
  workTypes,
  buildingTypes,
  setFormSaved,
  setIsContactsModalOpen,
  onSubmit,
  // projectId,
  cameFromSearch,
  activityCreated,
  onEditEbox,
}: Props) => {
  const { register, handleSubmit, errors, control, getValues, setValue, formState, watch } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    // shouldUnregister: false,
  });
  const { permitId, projectId } = useParams();
  const dispatch = useDispatch();
  const selectedPermit = useSelector((s: RootState) => s.projectsSlice.selectedPermit);
  const permitContactsRedux = useSelector((s: RootState) => s.projectsSlice.permitContacts);
  const permitFees = useSelector((s: RootState) => s.fees.permitFees);
  const [linkValue, setLinkValue] = useState<string>('');
  const [approvedPlansValue, setApprovedPlansValue] = useState<string>('');
  const {
    createOrUpdate,
    modalSuccess,
    modalError,
    isApiSuccess,
    successResponse,
    handleCloseModalInfo,
    modalErrorTitle,
    handleAdd,
    isModalOpen,
    modalTitle,
    handleCloseModal,
    values,
    innerModalErrors,
    setSearch,
    search,
    // tableManuallyUpdate,
  } = useTablePage({
    createEndpoint: createUser,
    modalCreateTitle: 'Add Contact',
    updateEndpoint: updateProject,
  });
  // const [users, setUsers] = React.useState(null)
  const [selectedContact, setSelectedContact] = React.useState(null);
  const [initialContact, setInitialContact] = React.useState(getValues('permitContacts'));

  const [isShowMore, setShowMore] = React.useState(false);
  const handleContactSelect = (newContacts, ids, data) => {
    setSelectedContact(newContacts);
    const contacts = getValues('permitContacts') || [];
    let tempContact;
    if (data) {
      if (contacts.findIndex(contact => contact?.id === data?.id) === -1) {
        tempContact = [...contacts];
        tempContact.push(data);
        setValue('permitContacts', tempContact);
      } else {
        if (newContacts.findIndex(contact => contact?.id === data?.id) === -1) {
          // tempContact=contacts.filter(contact => contact?.id! === data?.id)
          tempContact = [...newContacts];
          setValue('permitContacts', tempContact);
        }
      }
    }
  };
  const handleAddContact = () => {
    const _data = getValues('permitContacts') || [];

    const uniqData = _data.filter((v, i, a) => v !== undefined && a.findIndex(v2 => v2?.id === v?.id) === i);

    setAddUserModal(false);
    setValue('permitContacts', uniqData, { shouldDirty: true });
    setInitialContact(getValues('permitContacts'));
  };
  const [initialSelectedRows, setInitialSelectedRows] = React.useState({});
  const userColumns = column => {
    if (
      [
        'id',
        'firstName',
        'lastName',
        'phoneNumber',
        'address',
        'city',
        'state',
        'clientType',
        'phoneNumber',
        'parentCompany',
        'street',
        'mobilePhoneNumber',
        'userRole',
        'zipCode',
        'isEpsuperadmin',
        'isInvitationSent',
        'licenses',
        'boLicense',
        'archEngLicense',
        'prNumber',
        'isActive',
        'isDeleted',
        'trade',
        'tradesNames',
        'franchise',
        'franchiseNames',
        'avatar',
        'createdAt',
        'defaultFranchiseId',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'companyName':
        return 'Company Name';
      case 'email':
        return 'Email';
      case 'name':
        return 'Name';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const userColumnOrder = ['name', 'email', 'companyName'];
  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isContactFormSubmitting,
    setIsContactFormSubmitting,
  ] = React.useState(false);

  const [addUserModal, setAddUserModal] = React.useState(false);
  const [allUserData, setAllUserData] = React.useState(null);
  const [addNewUserModalOpen, setAddNewUserModalOpen] = React.useState(false);
  const [addContactModalTitle, setAddContactModalTitle] = React.useState('Add Contacts');

  const isInitialAddUserModal = React.useRef(true);
  React.useEffect(() => {
    if (isInitialAddUserModal.current) {
      isInitialAddUserModal.current = false;
      return;
    }
    setSearch('');
  }, [addUserModal]);
  const [isPermitCompleted, setIsPermitCompleted] = React.useState(true);

  React.useEffect(() => {
    if (isApiSuccess) {
      setAddNewUserModalOpen(false);
    }
  }, [isApiSuccess]);
  React.useEffect(() => {
    setInitialContact(getValues('permitContacts'));
  }, []);
  // Re-evaluate whether the permit may be marked Completed/Withdrawn whenever an
  // activity changes (activityCreated toggles on add/edit) or a fee is paid/sent/
  // deleted (permitFees reference changes on every Fees-tab reload). This effect used
  // to run only on mount, so the dropdown options stayed locked until the user
  // refreshed the page even after everything was completed. (EA-931)
  React.useEffect(() => {
    if (!permitId) return;
    getPermitActivitiesInspectionStatus(permitId).then(res => setIsPermitCompleted(res?.data?.data));
  }, [permitId, activityCreated, permitFees]);
  const modifiedGetUsers = params => {
    const searchTerm = search || params?.search || params?.searchColumn || '';
    if (!searchTerm || searchTerm.length < 2) {
      return Promise.resolve({
        data: { results: [], count: 0 },
      });
    }
    return getUsersCustom({
      limit: 25,
      userRole: USER_TYPES_VALUES.CLIENT,
      search: searchTerm,
      ...params,
    }).then(userRes => {
      const filteredUsers = userRes?.data?.filter(item => item?.clientType === CLIENT_TYPES_VALUES.INDIVIDUAL) || [];
      const contactsId = filteredUsers.map(item => item.id) || [];
      const permitContacts = getValues('permitContacts') || [];
      const contactsData = permitContacts?.map(contact => contact?.id) || [];
      const tempInitial = contactsId.reduce((acc, val, i) => {
        if (contactsData.length && contactsData.includes(val)) {
          //  acc = {...acc,i:true}
          acc[i] = true;
        }
        return acc;
      }, {});
      setInitialSelectedRows(tempInitial);
      return {
        ...userRes,
        data: {
          results: filteredUsers.map(item => {
            return {
              ...item,
              name: [item?.firstName, item?.lastName].filter(Boolean).join(' '),
              email: item?.email,
              companyName: item?.companyName || item?.parentCompany?.companyName || '',
            };
          }),
        },
      };
    });
  };
  React.useEffect(() => {
    if (projectId) {
      Promise.all([getProjectByID(projectId)])
        .then(([projectResponse]) => {
          setValue('projectName', projectResponse?.data?.name);
        })
        .catch(err => console.log(err))
        .finally(() => {});
    }
  }, [projectId, errors]);

  React.useEffect(() => {
    if (Object.keys(selectedPermit).length === 0) {
      return;
    }
    // Re-sync every form field whenever the source data changes (e.g. after a save).
    // `useForm({ defaultValues })` only applies on mount, and the Controller-managed
    // fields are handled by the per-field setValue calls below — but the plain text
    // inputs (Permit Number, Name, Square Feet, …) would keep showing the pre-save
    // value until a full page reload without this.
    //
    // NOTE: use targeted setValue() here, NOT reset(). reset() also wipes formState
    // (errors / isSubmitted / isSubmitSuccessful), re-runs react-hook-form internals
    // and re-mounts the input subtree; on this screen that feeds back into the
    // "reshape selectedPermit" effect + the isProjectLoading loader toggle and loops
    // ("Maximum update depth exceeded", React #185).
    if (initialValues) {
      setValue('name', initialValues.name);
      setValue('number', initialValues.number);
      setValue('total', initialValues.total);
      setValue('square', initialValues.square);
      setValue('inspections', initialValues.inspections);
      setValue('info', initialValues.info);
      setValue('folioId', initialValues.folioId);
      setValue('gateCode', initialValues.gateCode);
      setValue('permitLink', initialValues.permitLink);
      setValue('manager', initialValues.manager);
      setValue('address', initialValues.address);
      setValue('jurisdiction', initialValues.jurisdiction);
      setValue('description', initialValues.description);
      setValue('permitIssued', initialValues.permitIssued);
    }
    if (!initialValues?.storageBox) {
      setValue('storageBox', '0');
    }
    if (!initialValues?.permitCost) {
      setValue('permitCost', '0');
    }
    if (initialValues?.building) {
      // @ts-ignore
      setValue('building', initialValues.building);
    }
    if (initialValues?.work) {
      // @ts-ignore
      setValue('work', initialValues.work);
    }
    if (initialValues?.status) {
      setValue('status', initialValues.status);
    }
    if (initialValues?.serviceType) {
      setValue('serviceType', initialValues.serviceType);
    }
    if (initialValues?.expirationDate) {
      const date = Date.parse(initialValues.expirationDate);
      setValue('expirationDate', new Date(date));
    }

    Promise.all([
      getPermitUsers({ user_role: 3, permit_id: selectedPermit?.id || permitId }),
      selectedPermit?.contractor && getUserByID(selectedPermit?.contractor),
    ]).then(([permitUsers, contractor]) => {
      setValue('permitContacts', [...permitUsers?.data, contractor?.data]);
      dispatch(setPermitUsers(permitUsers?.data));
    });
  }, [initialValues, selectedPermit]);

  React.useEffect(() => {
    if (Object.keys(errors).length > 0) {
      Promise.all([
        getPermitUsers({ user_role: 3, permit_id: selectedPermit?.id }),
        selectedPermit?.contractor && getUserByID(selectedPermit?.contractor),
      ]).then(([permitUsers, contractor]) => {
        setValue('permitContacts', [...permitUsers?.data, contractor?.data]);
        dispatch(setPermitUsers(permitUsers?.data));
      });
    }
  }, [errors]);

  React.useEffect(() => {
    if (formState.isSubmitted && formState.isSubmitSuccessful) {
      setFormSaved(true);
    }
  }, [formState.isSubmitSuccessful, formState.isSubmitted]);

  const classes = useStyles();

  const onCurrencyChange = (value, name) => {
    setValue(name, value, { shouldDirty: true });
  };

  const formatContacts = contacts => {
    const getBooleanValue = value => value === true || value === 1 || value === 'true';
    if (contacts) {
      const res = contacts
        ?.map(contact => {
          const id = +contact.id;
          const isCompany = Number(contact?.clientType) === CLIENT_TYPES_VALUES.COMPANY;
          return {
            ...(isCompany ? { company_user: id } : { user: id }),
            contact_type: contact.contactType
              ? contactTypes.find(item => item?.name == contact?.contactType)?.id ??
                contactTypes.find(item => item?.id == contact?.contactType)?.id ??
                contactTypes[0]?.id
              : contactTypes[0]?.id,
            isAccounting: getBooleanValue(contact.isAccounting),
            isInspection: getBooleanValue(contact.isInspection),
          };
        })
        .filter(el => !!(el.user || el.company_user));
      return res;
    }
    return [];
  };
  const middleware = (submitFn: (data) => void) => data => {
    const contacts = getAccountingInfo(data.permitContacts, permitContactsRedux);
    return submitFn({
      id: selectedPermit?.id || permitId,
      name: data.name,
      number: data.number,
      amount: data?.total ? data?.total || 0 : 0,
      squareFeet: data.square,
      gateCode: data.gateCode,
      folioId: data.folioId,
      inspectionsCount: data.inspections ? data.inspections : 0,
      storageBox: data.storageBox,
      permitCost: data.permitCost,
      address: data.address || data.street + ',' + data.city + ',' + data.state + ',' + data.zipCode,
      description: data.description,
      info: data.info || '',
      status: +data.status.value || data.status.id,
      permitLink: linkValue,
      approvedPlans: approvedPlansValue,
      contacts: formatContacts(contacts),
      serviceType: data.serviceType.map(el => el.id).filter(el => el !== undefined),
      work: data.work.value ? +data.work.value : data.work.id,
      building: data.building?.value || data.building?.id,
      expirationDate: data.expirationDate ? `${formatDate(data.expirationDate)}` : null,
      permitIssued: data?.permitIssued,
    });
  };

  const toggleCollapse = () => {
    setShowMore(!isShowMore);
    setInitialContact(getValues('permitContacts'));
  };

  if (formState.isSubmitting) {
    return <LoaderWithContainer />;
  }
  return (
    <Container>
      <h2>Permit Details</h2>
      {initialValues && (
        <FormContainer autoComplete="off" aria-autocomplete="none">
          <InputBaseContainer>
            <Controller
              as={InputTextWithLabel}
              InputProps={{
                readOnly: true,
              }}
              name="projectName"
              defaultValue=""
              control={control}
              error={errors.projectName}
              label={labels.projectName}
              isRequired={isYupFieldRequired('projectName', validationSchema)}
            />
            <Controller
              as={InputTextWithLabel}
              InputProps={{
                readOnly: cameFromSearch,
              }}
              name="name"
              defaultValue=""
              control={control}
              error={errors.name}
              label={labels.name}
              isRequired={isYupFieldRequired('name', validationSchema)}
            />
            <InputTextWithLabel
              name="number"
              error={errors.number}
              inputRef={register}
              label={labels.number}
              // EA-221
              onChange={event => {
                setValue('number', event.target.value.slice(0, 200));
              }}
              isRequired={isYupFieldRequired('number', validationSchema)}
              disabled={cameFromSearch}
            />
            <Controller
              as={InputTextWithLabel}
              name="address"
              defaultValue=""
              InputProps={{ readOnly: true }}
              control={control}
              error={errors.address}
              label={labels.address}
              isRequired={isYupFieldRequired('address', validationSchema)}
            />
          </InputBaseContainer>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ButtonMain style={{ marginTop: 10, width: 140 }} variant="outlined" onClick={toggleCollapse}>
              {isShowMore ? 'Hide' : 'Show More'}
            </ButtonMain>
          </div>

          <Collapse
            isOpened={isShowMore}
            initialStyle={{
              height: '0px',
              overflow: 'hidden',
              transition: '0.4s',
            }}
          >
            <div style={{ height: 'auto' }}>
              <InputBaseContainer>
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  render={({ value, onChange }) => (
                    <RichTextEditorWithLabel
                      label={labels.description}
                      value={value}
                      onChange={onChange}
                      error={errors.description}
                      isRequired={isYupFieldRequired('description', validationSchema)}
                      disabled={cameFromSearch}
                    />
                  )}
                />

                <Controller
                  name="permitContacts"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <ContactsWrapper>
                      <InputSelectWithLabel
                        // onFocus={() => getAllUsers()}
                        isMulti
                        placeholder=""
                        showCompany
                        menuPlacement="top"
                        label={labels.permitContacts}
                        error={errors.permitContacts}
                        options={allUserData ? allUserData : []}
                        getOptionValue={(item: any) => item.id}
                        getOptionLabel={(item: any) => {
                          const parts = [
                            [item.firstName, item.lastName].filter(Boolean).join(' '),
                            item?.email,
                            item?.companyName || item?.parentCompany?.companyName,
                          ].filter(Boolean);
                          return parts.join(' - ');
                        }}
                        isRequired={isYupFieldRequired('permitContacts', validationSchema)}
                        isDisabled={cameFromSearch}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...props}
                      />
                    </ContactsWrapper>
                  )}
                />
                <ButtonsWrapper>
                  <ButtonContainer>
                    <StyledButtonMain variant="outlined" onClick={() => setAddUserModal(true)} disabled={cameFromSearch}>
                      Add Contact
                    </StyledButtonMain>
                  </ButtonContainer>
                  <ButtonContainer>
                    <StyledButtonMain variant="outlined" onClick={() => dispatch(setIsViewContactsModal(true))}>
                      View Contacts
                    </StyledButtonMain>
                  </ButtonContainer>
                </ButtonsWrapper>
              </InputBaseContainer>
              <InputBaseContainer>
                {/* EA-1373: Folder Name — auto-generated by the backend on
                    permit creation (e.g. NPL2026080001), read-only. Placed to
                    the left of Jurisdiction so Folder Name | Jurisdiction |
                    Project Manager share one equal-width row. */}
                <InputTextWithLabel
                  name="folderName"
                  label={labels.folderName}
                  value={initialValues?.folderName || ''}
                  InputProps={{ readOnly: true }}
                />
                <Controller
                  as={InputTextWithLabel}
                  InputProps={{
                    readOnly: true,
                  }}
                  name="jurisdiction"
                  defaultValue=""
                  control={control}
                  error={errors.jurisdiction}
                  label={labels.jurisdiction}
                  isRequired={isYupFieldRequired('jurisdiction', validationSchema)}
                />
                <Controller
                  as={InputTextWithLabel}
                  InputProps={{
                    readOnly: true,
                  }}
                  name="manager"
                  defaultValue=""
                  control={control}
                  error={errors.manager}
                  label={labels.manager}
                  isRequired={isYupFieldRequired('manager', validationSchema)}
                />
              </InputBaseContainer>
              <InputBaseContainer>
                <Controller
                  name="status"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputSelectWithLabel
                      placeholder=""
                      menuPlacement="top"
                      label={labels.status}
                      error={errors.status}
                      isOptionDisabled={option => isPermitCompleted && (option.title === 'Completed' || option.title === 'Withdrawn')}
                      options={permitStatus as any}
                      getOptionValue={(item: any) => (item.value ? item.value : item.id)}
                      getOptionLabel={(item: any) => (item.label ? item.label : item.title)}
                      isRequired={isYupFieldRequired('status', validationSchema)}
                      isDisabled={cameFromSearch}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
                <Controller
                  name="serviceType"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputSelectWithLabel
                      isMulti
                      selectAllActive
                      placeholder=""
                      menuPlacement="top"
                      label={labels.serviceType}
                      error={errors.serviceType}
                      options={serviceTypes as any}
                      getOptionValue={(item: any) => item.id}
                      getOptionLabel={(item: any) => (item.name ? item.name : item.title)}
                      isRequired={isYupFieldRequired('serviceType', validationSchema)}
                      isDisabled={cameFromSearch}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
              </InputBaseContainer>
              <InputBaseContainer>
                <Controller
                  name="work"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputSelectWithLabel
                      placeholder=""
                      menuPlacement="top"
                      label={labels.work}
                      error={errors.work}
                      getOptionValue={(item: any) => (item.value ? item.value : item.id)}
                      getOptionLabel={(item: any) => (item.label ? item.label : item.title)}
                      options={workTypes as any}
                      isRequired={isYupFieldRequired('work', validationSchema)}
                      isDisabled={cameFromSearch}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
                <Controller
                  name="building"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputSelectWithLabel
                      placeholder=""
                      menuPlacement="top"
                      label={labels.building}
                      error={errors.building}
                      options={buildingTypes as any}
                      getOptionValue={(item: any) => (item.value ? item.value : item.id)}
                      getOptionLabel={(item: any) => (item.label ? item.label : item.title)}
                      isRequired={isYupFieldRequired('building', validationSchema)}
                      isDisabled={cameFromSearch}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
                <Controller
                  name="total"
                  defaultValue=""
                  control={control}
                  render={props => (
                    <InputCurrencyWithLabel
                      allowDecimals
                      error={errors.total}
                      label={labels.total}
                      onCurrencyChange={onCurrencyChange}
                      isRequired={isYupFieldRequired('total', validationSchema)}
                      disabled={cameFromSearch}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
              </InputBaseContainer>
              <InputBaseContainer>
                <TwinControls>
                  <InputTextWithLabel
                    InputProps={{
                      readOnly: cameFromSearch,
                    }}
                    name="inspections"
                    error={errors.inspections}
                    inputRef={register}
                    label={labels.inspections}
                    isRequired={isYupFieldRequired('inspections', validationSchema)}
                  />
                  <InputTextWithLabel
                    InputProps={{
                      readOnly: cameFromSearch,
                    }}
                    name="folioId"
                    error={errors.folioId}
                    inputRef={register}
                    label={labels.folio}
                  />
                </TwinControls>
                <TwinControls>
                  <InputTextWithLabel
                    name="square"
                    error={errors.square}
                    inputRef={register}
                    label={labels.square}
                    isRequired={isYupFieldRequired('square', validationSchema)}
                    disabled={cameFromSearch}
                  />
                  <InputTextWithLabel
                    name="gateCode"
                    error={errors.gateCode}
                    inputRef={register}
                    label={labels.gateCode}
                    disabled={cameFromSearch}
                  />
                </TwinControls>
                <TwinControls>
                  <InputTextWithLabel
                    name="storageBox"
                    error={errors.storageBox}
                    inputRef={register}
                    label={labels.storageBox}
                    isRequired={isYupFieldRequired('storageBox', validationSchema)}
                    disabled={cameFromSearch}
                  />
                  <InputTextWithLabel
                    name="permitCost"
                    error={errors.permitCost}
                    inputRef={register}
                    label={labels.permitCost}
                    disabled={cameFromSearch}
                  />
                </TwinControls>
                <Controller
                  as={TextAreaWithLabel}
                  name="info"
                  defaultValue=""
                  control={control}
                  error={errors.info}
                  label={labels.info}
                  isRequired={isYupFieldRequired('info', validationSchema)}
                  disabled={cameFromSearch}
                />
              </InputBaseContainer>
              <BottomFieldsGrid>
                <EboxColumn>
                  <EboxStatusFill>
                    <EboxStatus confirmationNumber={initialValues?.eboxConfirmationNumber} />
                  </EboxStatusFill>
                  {onEditEbox && (
                    <EditEboxButton type="button" onClick={onEditEbox}>
                      {initialValues?.eboxConfirmationNumber ? 'Edit Ebox' : 'Add Ebox'}
                    </EditEboxButton>
                  )}
                </EboxColumn>
                <Controller
                  name="expirationDate"
                  defaultValue=""
                  valueName="selected"
                  onChange={([selected]) => selected}
                  control={control}
                  menuPlacement="top"
                  render={props => (
                    <InputDatePickerWithLabel
                      label={labels.expirationDate}
                      error={errors.expirationDate}
                      dateFormat={DEFAULT_DATE_FORMAT}
                      // disabled={isReadOnly || initialValues?.isCompleted}
                      isRequired={isYupFieldRequired('expirationDate', validationSchema)}
                      // eslint-disable-next-line react/prop-types
                      selected={props.value}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    />
                  )}
                />
                <PermitFormPermitLink
                  initValues={initialValues}
                  setLinkValue={setLinkValue}
                  name="permitLink"
                  disabled={cameFromSearch}
                />
                <PermitFormPermitLink
                  initValues={initialValues}
                  setLinkValue={setApprovedPlansValue}
                  name="approvedPlans"
                  label={labels.approvedPlans}
                  valueKey="approvedPlans"
                  disabled={cameFromSearch}
                  allowClear
                />
                <PermitIssuedField>
                  <PermitIssuedLabel>{labels.permitIssued}</PermitIssuedLabel>
                  <Controller
                    name="permitIssued"
                    control={control}
                    valueName="checked"
                    defaultValue={selectedPermit?.permitIssued ?? false}
                    render={({ onChange, value }) => (
                      <SwitchCheckbox
                        name="permitIssued"
                        value=""
                        isRadio={false}
                        title={labels.permitIssued}
                        label={value ? 'Issued' : 'Not issued'}
                        checked={!!value}
                        disabled={cameFromSearch}
                        onChange={payload => onChange(payload.target.checked)}
                      />
                    )}
                  />
                </PermitIssuedField>
              </BottomFieldsGrid>
              <ButtonWithLoader
                variant="contained"
                color="primary"
                type="submit"
                onClick={handleSubmit(middleware(onSubmit))}
                className={classes.button}
                disabled={!formState.isDirty || !!Object.keys(errors).length || cameFromSearch}
                loading={formState.isSubmitting && !Object.keys(errors).length}
              >
                Save
              </ButtonWithLoader>
            </div>
          </Collapse>
        </FormContainer>
      )}
      {addUserModal && (
        <StyledModalDraggable
          onClose={() => {
            if (addNewUserModalOpen) {
              setAddNewUserModalOpen(false);
              setAddContactModalTitle('Add Contacts');
            } else {
              setAddUserModal(false);
              setAddContactModalTitle('Add Contacts');
              setValue('permitContacts', initialContact);
            }
          }}
          title={addContactModalTitle}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {!addNewUserModalOpen && (
              <>
                <ActionsPanel
                  isBreadCrumbsVisible={false}
                  onSearch={setSearch}
                  isActionPanelMoreVisible={false}
                  className="action-container"
                />
              </>
            )}
            {!addNewUserModalOpen && (
              <>
                <ButtonWithLoader
                  style={{ float: 'right' }}
                  variant="contained"
                  color="primary"
                  onClick={() => setAddNewUserModalOpen(true)}
                  loading={false}
                >
                  New
                </ButtonWithLoader>
                <br />
              </>
            )}
          </div>
          {addNewUserModalOpen && (
            <AddContactWizard
              modalError={modalErrorTitle.length > 0}
              initialErrors={innerModalErrors}
              onSubmit={createOrUpdate}
              setModalTitle={setAddContactModalTitle}
            />
          )}
          {(isApiSuccess || modalError) && (
            <ModalInfo title="Success" timeToClose={5000} isError={modalError} onClose={handleCloseModalInfo} errorMsg={modalErrorTitle} />
          )}

          {!addNewUserModalOpen && (!search || search.length < 2) && (
            <SearchHint>
              <SearchHintTitle>Start typing to find contacts</SearchHintTitle>
              <SearchHintBody>
                Enter at least <strong>2 characters</strong> in the search box above to look up contacts by name or email.
              </SearchHintBody>
            </SearchHint>
          )}
          {!addNewUserModalOpen && search && search.length >= 2 && (
            <>
              <ContactSearchList
                searchQuery={search}
                selectedIds={(getValues('permitContacts') || []).map((c: any) => c?.id).filter(Boolean)}
                onToggle={contact => {
                  const current = getValues('permitContacts') || [];
                  const isSelected = current.some((c: any) => c?.id === contact.id);
                  const next = isSelected ? current.filter((c: any) => c?.id !== contact.id) : [...current, contact];
                  setValue('permitContacts', next);
                  setSelectedContact(next);
                }}
              />
              <br />
              <ButtonWithLoader
                variant="outlined"
                // color="primary"
                onClick={() => setAddUserModal(false)}
                loading={false}
              >
                Cancel
              </ButtonWithLoader>{' '}
              <ButtonWithLoader
                variant="contained"
                color="primary"
                // type="submit"
                // style={{marginLeft: ''}}
                onClick={() => handleAddContact()}
                loading={false}
              >
                Ok
              </ButtonWithLoader>{' '}
            </>
          )}
          {/* </InfiniteScroll> */}
        </StyledModalDraggable>
      )}
    </Container>
  );
};

interface Props {
  initialValues: InitialValues;
  selectedPermit: PermitType;
  permitStatus: VocabularyDataType;
  allContacts: UserType;
  serviceTypes: VocabularyDataType;
  workTypes: VocabularyDataType;
  buildingTypes: VocabularyDataType;
  setFormSaved: (data: boolean) => void;
  isLoading: boolean;
  contactTypes: Array<ContactTypesType>;
  setIsContactsModalOpen: (data: boolean) => void;
  onSubmit: (data: VocabularyDataType) => void;
  projectId?: number;
  activityCreated?: boolean;
  onEditEbox?: () => void;
}

interface InitialValues {
  name: string;
  number?: string;
  total?: string;
  square?: string;
  gateCode?: string;
  permitLink?: string;
  approvedPlans?: string;
  inspections: number;
  folio?: string;
  storageBox?: string;
  permitCost?: number;
  jurisdiction: string;
  documents?: any;
  address: string;
  description: string;
  info?: string;
  status?: any;
  permitContacts: { user: number; contactType: number }[];
  // EA-1373: auto-generated folder name (e.g. NPL2026080001), read-only.
  folderName?: string;
  serviceType?: Array<VocabularyDataType>;
  manager: string;
  work?: Array<VocabularyDataType>;
  building?: Array<VocabularyDataType>;
}

const Container = styled.fieldset`
  display: block;
  position: relative;
  margin: 0;
  padding: 15px 20px 30px 20px;
  width: 100%;
  box-sizing: border-box;
  border: none;
  text-align: center;
  // min-height: 10vh;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
`;

const ButtonContainer = styled.div`
  width: min-content;
  margin-left: 10px;
  margin-top: 4px;
`;

const StyledModalDraggable = styled(ModalDefaultDraggable)`
  overflow-y: auto;
  height: 100%;
  text-align: center;
`;

const StyledButtonMain = styled(ButtonMain)`
  &.MuiButton-root {
    line-height: 1.15;
  }
`;

const ContactsWrapper = styled.div`
  max-width: 500px;
`;

const BottomFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px 24px;
  align-items: start;
  width: 100%;
  padding: 8px 0;
`;

const EboxColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;

  /* When the column gets narrow (tablet/stacked grid), drop the Edit button
     below the card so the EBOX number stops truncating to "E…". */
  @media (max-width: 1180px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const PermitIssuedField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  & > div:last-child {
    display: flex;
    align-items: center;
    height: 40px;
  }
`;

const PermitIssuedLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 8px;
`;

const EboxStatusFill = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const EditEboxButton = styled.button`
  flex-shrink: 0;
  min-width: 90px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: var(--fw-semibold);
  font-size: var(--fs-base);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const TwinControls = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;

  > * {
    flex: 1 1 240px;
    min-width: 0;
  }

  @media (max-width: 540px) {
    flex-direction: column;
    gap: 8px;

    > * {
      flex: 0 1 auto;
    }
  }
`;

const SearchHint = styled.div`
  text-align: center;
  padding: 32px 16px;
  margin-top: 12px;
  background: var(--neutral-50);
  border: 1px dashed var(--border);
  border-radius: 10px;
  color: var(--neutral-600);
`;

const SearchHintTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 6px;
`;

const SearchHintBody = styled.div`
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--neutral-600);

  strong {
    color: var(--mainText);
  }
`;

export default PermitForm;
